import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useUnifiedList } from '../../ActivitiesPage/hooks/useUnifiedList';
import { useActivitiesFromSchedule } from '../../ActivitiesPage/hooks/useActivitiesFromSchedule';
import { useBootstrapSchedule } from '../../ActivitiesPage/hooks/useBootstrapSchedule';
import { DEFAULT_FILTERS } from '../../ActivitiesPage/activitiesFilter';
import { ActivityDrawer } from '../../ActivitiesPage/Drawer/ActivityDrawer';
import { RiskAssessment } from '../../ActivitiesPage/Drawer/RiskAssessment';
import { TaskPanel } from '../../SchedulerPage/components/TaskPanel/TaskPanel';
import { ActivityLadderContext, usePageActivityLadder } from '../../SchedulerPage/hooks/useActivityLadder';
import { useSchedulerActions, useSchedulerSite } from '../../SchedulerPage/hooks/useSchedulerStore';
import type { ISchedulerTask } from '@interfaces/IScheduler';
import { Drawer } from '@components/ui/Drawer/Drawer';
import { LoaderIcon } from '@utils/icons';
import styles from './alertDrawerHost.module.css';

// Stable empty plan for the ladder before the schedule is in the store.
const NO_TASKS: ISchedulerTask[] = [];

export interface IAlertDrawerHostProps {
  siteId: string;
  /** An `IListItem.key` (`act:<taskId>` / `iss:<issueId>`) — the alert row's
   *  identity, resolved against the unfiltered unified list below. */
  itemKey: string;
  /** The activity's schedule, when the opener knows it (a productivity row
   *  names its own); otherwise the TopBar's active schedule. */
  scheduleNumber?: string;
  onClose: () => void;
}

/**
 * Opens a single alert row's (or Monitor list card's) detail IN PLACE, using the same
 * surfaces the rest of the product uses for the same entity:
 *
 * - an ACTIVITY opens the shared Scheduler `TaskPanel` (identical to what the
 *   Scheduler and the Activities & Issues page show on task click), with the
 *   scheduler store bootstrapped from the active schedule exactly the way
 *   ActivitiesPage does it;
 * - an ISSUE opens the Activities page's own `ActivityDrawer` (issues have no
 *   TaskPanel — same split as ActivitiesPage).
 *
 * `useUnifiedList` / the schedule detail are the expensive queries the
 * Activities page runs — this component pays that cost, so its callers (the
 * Dashboard's ActivityAlertsCard, the Monitor's LayerList) must render it ONLY
 * while a row's drawer is open (their open-key state), never unconditionally.
 */
export function AlertDrawerHost({ siteId, itemKey, scheduleNumber: openerScheduleNumber, onClose }: IAlertDrawerHostProps) {
  const { t } = useTranslation();
  const isIssue = itemKey.startsWith('iss:');
  const taskId = isIssue ? null : itemKey.replace(/^act:/, '');

  // The unfiltered pool — resolves the issue item, and the activity's
  // already-computed risk for the panel's Overview slot.
  const { allItems, activitiesLoading, issuesLoading } = useUnifiedList(siteId, DEFAULT_FILTERS);
  const item = useMemo(
    () => allItems.find((candidate) => candidate.key === itemKey) ?? null,
    [allItems, itemKey],
  );

  // --- Activity path: bootstrap the scheduler store, as ActivitiesPage does,
  // then the shared panel.
  const activitySiteId = isIssue ? undefined : siteId;
  const { detail, scheduleNumber } = useActivitiesFromSchedule(activitySiteId, openerScheduleNumber);
  const { updateTask } = useSchedulerActions();
  const site = useSchedulerSite(siteId);
  useBootstrapSchedule(activitySiteId, detail, scheduleNumber);
  // The live activity feed's word for each task, so the panel's ladder reads
  // Trusstor's status (in progress, overdue…) — and offers the actions that go
  // with it — wherever the drawer is opened from, not only under a page that
  // already provides it.
  const { store: ladder } = usePageActivityLadder(activitySiteId, site?.tasks ?? NO_TASKS);

  // Same safety handler ActivitiesPage provides (structural edits are hidden
  // via allowStructureEdit={false}, so this should never actually fire).
  const handleTaskPanelSave = useCallback(
    (task: ISchedulerTask) => updateTask({ siteId, task }),
    [siteId, updateTask],
  );

  const riskSlot = !isIssue && item ? (
    <RiskAssessment onTime={item.onTimeRisk} safety={item.safety} />
  ) : undefined;

  // "Ready" also requires the task to EXIST in the loaded schedule — an alert
  // for a since-deleted task must show the unavailable state, not an empty
  // panel aimed at a bogus id.
  const activityExists = !isIssue && detail != null && detail.tasks.some((task) => task.id === taskId);
  // The store must hold THIS schedule's task, not merely a schedule: TaskPanel
  // reads its task from the store once, on open, so opening it against a store
  // still holding the previously bootstrapped schedule renders nothing for good.
  const activityReady = activityExists && site != null && site.tasks.some((task) => task.id === taskId);
  const issueReady = isIssue && item !== null;
  const loading = isIssue
    ? issuesLoading && !item
    : activitiesLoading || detail == null || (activityExists && !activityReady);

  if (activityReady && site) {
    return (
      <ActivityLadderContext.Provider value={ladder}>
        <TaskPanel
          open
          mode="edit"
          data={site}
          scheduleNumber={scheduleNumber}
          taskId={taskId ?? undefined}
          allowStructureEdit={false}
          overviewSlot={riskSlot}
          onClose={onClose}
          onSave={handleTaskPanelSave}
        />
      </ActivityLadderContext.Provider>
    );
  }
  if (issueReady) {
    return <ActivityDrawer item={item!} onClose={onClose} />;
  }

  const statusLabel = t(
    loading ? 'dashboard.activityAlerts.drawer.loading' : 'dashboard.activityAlerts.drawer.unavailable',
  );

  // A lightweight stand-in while the data resolves (or, rarely, when the alert's
  // item can no longer be found), on the shared right-side drawer shell.
  return (
    <Drawer onClose={onClose} ariaLabel={statusLabel} closeLabel={t('activities.drawer.close')}>
      <div className={styles.state} aria-busy={loading}>
        {loading && (
          <span className={styles.spinner} aria-hidden="true">
            <LoaderIcon size={22} />
          </span>
        )}
        <span>{statusLabel}</span>
      </div>
    </Drawer>
  );
}
